import { createActor, fromPromise, setup } from 'xstate'
import { spinService } from '../src/domain/game'
import { placeOrderService } from '../src/domain/placeOrder'
import { useGameStore } from '../src/domain/game/store/GameStore'
import {
  autoPlayService, ga4Info, preciseAdd,
  preciseDivide, preciseMultiply, soundManager, useAutoPlayStore,
  useChipSetStore,
  useIsSpinning,
  usePlayerStore,
  useSpeedStore, useVoucherStore,
} from '@slot-nx/core'
import { resultService } from '../src/domain/result'
import { multiplierService } from '../src/domain/spin/service/MultiplierService'
import { winBarService } from '../src/domain/winning/service/winbarService'
import { sleep } from '../src/domain/game/util/gameUtil'
import { GodziDropResultResponse, SlotSymbolInfo, WinReelResult } from '../src/domain/placeOrder/api/PlaceOrderResponse'
import { FreeGameType } from '../src/domain/buybonus/enum/buyBonusEnum'
import { buyBonusService } from '../src/domain/buybonus'
import { SymbolType } from '../src/domain/game/enum/SymbolType'
import { freeGameService } from '../src/domain/freeGame/service/freeGameService'
import { winningPanelService } from '../src/domain/winning/service/winningPanelService'
import { gameConfig } from '../src/config'
import { useGodziDropStore } from '../src/domain/godziDrop/store/godziDropStore'
import { GodziType } from '../src/domain/godziDrop/enum/godziDropEnum'
import { godziDropService } from '../src/domain/godziDrop/service/GodziDropService'
import { preciseMinus } from '@slot-nx/core'
import { godziDashboardService } from '../src/domain/godziDrop/service/GodziDashboardService'

export enum MachineActionType {
  gameReady = 'gameReady',
  placeOrder = 'placeOrder',
  freeGameSpinStart = 'freeGameSpinStart',
  freeGameSpinEnd = 'freeGameSpinEnd',
  godziDropStart = 'godziDropStart',
  godziDropEnd = 'godziDropEnd',
  voucherTimeStart = 'voucherTimeStart',
  voucherTimeEnd = 'voucherTimeEnd',
  enterVoucherMachine = 'enterVoucherMachine',
  godziPopupClosed = 'godziPopupClosed',
  voucherBackToMainGame = 'voucherBackToMainGame',
}

export enum GameState {
  init = 'init',
  initGodziDashBoard = 'initGodziDashBoard',
  mainGame = 'mainGame',
  placeOrderProcess = 'placeOrderProcess',
  placeOrderFail = 'placeOrderFail',
  displayGameResult = 'displayGameResult',
  elimination = 'elimination',
  drop = 'drop',
  totalWin = 'totalWin',
  freeGameEntry = 'freeGameEntry',
  freeGame = 'freeGame',
  freeGameEnd = 'freeGameEnd',
  godziEntry = 'godziEntry',
  godziTime = 'godziTime',
  godziEnd = 'godziEnd',
  // voucher
  voucher = 'voucher',
  voucherEntry = 'voucherEntry',
  voucherTime = 'voucherTime',
  voucherPlaceOrderProcess = 'voucherPlaceOrderProcess',
  voucherPlaceOrderFail = 'voucherPlaceOrderFail',
  voucherDisplayGameResult = 'voucherDisplayGameResult',
  voucherElimination = 'voucherElimination',
  voucherDrop = 'voucherDrop',
  voucherTotalWin = 'voucherTotalWin',
  voucherEnd = 'voucherEnd',
  voucherFreeGameEntry = 'voucherFreeGameEntry',
  voucherFreeGame = 'voucherFreeGame',
  voucherFreeGameEnd = 'voucherFreeGameEnd',
}
const voucherMachine = {
  initial: GameState.voucherEntry,
  states: {
    voucherEntry: {
      entry: ['showVoucherSelectPopup', 'updateVoucher'] as any,
      on: {
        [MachineActionType.voucherTimeStart]: {
          target: GameState.voucherTime,
        },
        [MachineActionType.voucherBackToMainGame]: {
          target: '#sweetBomb.mainGame',
          actions: ['setExitVoucherTime'],
        },
      },
    },
    voucherTime: {
      entry: ['setIsVoucherTime'] as any,
      on: {
        [MachineActionType.placeOrder]: GameState.voucherPlaceOrderProcess,
      },
    },
    voucherPlaceOrderProcess: {
      entry: ['resetPlaceOrderData', 'getSpeedModeStatus'] as any,
      invoke: {
        src: 'onPlaceOrderProcess' as any,
        onDone: GameState.voucherDisplayGameResult,
        onError: {
          target: GameState.voucherPlaceOrderFail,
        },
      },
    },
    voucherPlaceOrderFail: {
      invoke: {
        src: 'onPlaceOrderFail' as any,
        onDone: GameState.voucherTime,
      },
    },
    voucherEnd: {
      entry: ['showVoucherTotalWinPopup'] as any,
      on: {
        [MachineActionType.voucherBackToMainGame]: '#sweetBomb.mainGame',
        [MachineActionType.voucherTimeStart]: GameState.voucherEntry,
      },
      exit: ['setExitVoucherTime'] as any,
    },
    voucherDisplayGameResult: {
      entry: ['updateCurrentGameResult'] as any,
      invoke: {
        src: 'onDisplayGameResult' as any,
        onDone: [
          {
            target: GameState.voucherElimination,
            guard: 'hasDrop' as any,
          },
          {
            target: GameState.voucherTotalWin,
          },
        ],
      },
    },
    voucherElimination: {
      invoke: {
        src: 'onElimination' as any,
        onDone: GameState.voucherDrop,
      },
    },
    voucherDrop: {
      invoke: {
        src: 'onDrop' as any,
        onDone: [
          {
            target: GameState.voucherElimination,
            guard: 'hasDrop' as any,
          },
          {
            target: GameState.voucherTotalWin,
          },
        ],
      },
    },
    voucherTotalWin: {
      invoke: {
        src: 'onTotalWin' as any,
        onDone: [
          {
            guard: 'isFreeGameStart' as any,
            target: GameState.voucherFreeGameEntry,
          },
          {
            guard: 'hasFreeGameNextRound' as any,
            target: GameState.voucherFreeGame,
          },
          {
            guard: 'isFreeGameEnd' as any,
            target: GameState.voucherFreeGameEnd,
          },
          {
            guard: 'hasRemainSpinAndVoucher' as any,
            target: GameState.voucherPlaceOrderProcess,
          },
          {
            guard: 'hasRemainVoucher' as any,
            target: GameState.voucherTime,
          },
          {
            target: GameState.voucherEnd,
          },
        ],
      },
      exit: ['resetSpinBtn'] as any,
    },
    voucherFreeGameEntry: {
      entry: ['onEntryFreeGameEntry'] as any,
      on: {
        [MachineActionType.freeGameSpinStart]: {
          target: GameState.voucherFreeGame,
        },
      },
    },
    voucherFreeGame: {
      entry: ['updateFreeGameState', 'getSpeedModeStatus'] as any,
      invoke: {
        src: 'onFreeGame' as any,
        onDone: GameState.voucherDisplayGameResult,
      },
    },
    voucherFreeGameEnd: {
      entry: ['onEntryFreeGameEnd'] as any,
      on: {
        [MachineActionType.freeGameSpinEnd]: GameState.voucherTime,
        [MachineActionType.voucherTimeEnd]: GameState.voucherEnd,
      },
      exit: ['setIsMainGame', 'onExitFreeGameEnd'] as any,
    },
  },
}
const machine = setup({
  types: {
    events: {} as
    | { type: MachineActionType.gameReady }
    | { type: MachineActionType.placeOrder }
    | { type: MachineActionType.freeGameSpinStart }
    | { type: MachineActionType.freeGameSpinEnd }
    | { type: MachineActionType.godziDropEnd }
    | { type: MachineActionType.godziDropStart }
    | { type: MachineActionType.voucherTimeStart }
    | { type: MachineActionType.voucherTimeEnd }
    | { type: MachineActionType.godziPopupClosed }
    | { type: MachineActionType.enterVoucherMachine }
    | { type: MachineActionType.voucherBackToMainGame },
  },
  actors: {
    onPlaceOrderProcess: fromPromise(async () => {
      const freeGameType = useGameStore.getState().currentFreeGameType
      if (useAutoPlayStore.getState().isPlaying) {
        usePlayerStore.getState().updateBalanceToSettledAmount()
        await sleep(gameConfig.autoPlaySpinIntervalMs)
        autoPlayService.reduceRemain()

        ga4Info.recordAutoPlay()
      }

      spinService.openPlaceOrderLoading()
      const spin = spinService.spin()
      const placeOrder = freeGameType === FreeGameType.MainGame
        ? placeOrderService.placeOrder()
        : buyBonusService.buyBonus()

      const { isEnabled } = useSpeedStore.getState()

      if (isEnabled) {
        ga4Info.recordSpeedTurboMode()
      }

      await multiplierService.clearReel()
      winBarService.closeTotalWinBar()
      useGameStore.setState({
        freeGameAccumulatedPayout: 0,
      })
      await Promise.all([spin, placeOrder])
      spinService.closePlaceOrderLoading()

      const { currentFreeGameType, isAnteBet } = useGameStore.getState()
      if (currentFreeGameType === FreeGameType.MainGame) {
        if (isAnteBet) {
          ga4Info.recordPlaceExtraBet()
        }
        else {
          ga4Info.recordPlaceMainBet()
        }
      }
      else {
        ga4Info.recordPlaceBonusBet()
      }
    }),
    onPlaceOrderFail: fromPromise(async () => {
      // multiplierService.setMultiplierResult(true)
      // multiplierService.insertReel()
      // await placeOrderService.remainTable()
      // spinService.closePlaceOrderLoading()
    }),
    onFreeGame: fromPromise(async () => {
      await sleep(gameConfig.autoPlaySpinIntervalMs)
      useGameStore.setState(state => ({
        freeGameCount: state.freeGameCount - 1,
      }))
      const spin = spinService.spin()
      const promises = [spin]

      if (useGameStore.getState().isFirstFreeSpin()) {
        const clearMuiltplierReel = multiplierService.clearReel()
        promises.push(clearMuiltplierReel)
      }
      await Promise.all(promises)
    }),
    onDisplayGameResult: fromPromise(async () => {
      useIsSpinning.setState(true)
      if (useGameStore.getState().isFirstFreeSpin()) {
        if (useGameStore.getState().isFreeGame) {
          useGameStore.setState({
            multiplierReelInfo: useGameStore.getState().gameResult.freeGame[0].multiplierReel,
          })
        }
        multiplierService.setMultiplierResult(false)
        multiplierService.insertReel()
      }
      await spinService.dropNormalGameResult()
      useIsSpinning.setState(false)
    }),
    onElimination: fromPromise(async () => {
      useGameStore.setState({ isDisableEmergencyStop: true })
      const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
      resultService.onWinningSymbolHandle()
      await resultService.showBombAnimation()
      winBarService.updateOriginalPayout()

      if (!isThisSpinInSpeedMode) {
        await resultService.showSymbolWinning()
        resultService.showSymboLoop()
        await multiplierService.showActiveMultiplier()
      }

      const removeActiveMultipliePromise = multiplierService.removeActiveMultiplier()

      const updateMultipliedPayoutPromise = winBarService.updateMultipliedPayout()
      await Promise.all([removeActiveMultipliePromise, updateMultipliedPayoutPromise])
      await resultService.showSymbolEliminate()

      await resultService.removeSpine()

      multiplierService.moveOneSymbol()

      const isVoucherTime = useGameStore.getState().isVoucherTime
      if (isVoucherTime) {
        useVoucherStore.setState(state => ({
          totalPayout: preciseAdd(state.totalPayout, useGameStore.getState().currentPayout),
        }))
      }
    }),
    onDrop: fromPromise(async () => {
      await resultService.onDropHandle()
      updateCurrentDropResult()
    }),
    onTotalWin: fromPromise(async () => {
      const spinAccumulatedPayout = useGameStore.getState().spinAccumulatedPayout
      const gameResult = getGameResult()

      if (spinAccumulatedPayout > 0) {
        const currentStake = useChipSetStore.getState().computed.currentStake
        const odds = preciseDivide(spinAccumulatedPayout, currentStake)
        if (odds >= 20) {
          await winningPanelService.showWinningPanel(odds, spinAccumulatedPayout)
          soundManager.playBGM()
        }
      }
      const isFreeGameOnGoing = useGameStore.getState().freeGameSpinCurrentIndex < gameResult.freeGame.length - 1
      if (!isFreeGameOnGoing) {
        winBarService.updateFinalBalance()
      }
      await resultService.checkWinScatter()
      multiplierService.saveRemainReel()
    }),
    onGodziTime: fromPromise(async () => {
      let isGodziEnd = false
      let isFirstEntryGodziTime = false
      const { godziResult, isGodziTime, godziRoundIndex } = useGodziDropStore.getState()
      if (!isGodziTime) {
        useGodziDropStore.setState({ isGodziTime: true })
        isFirstEntryGodziTime = true
      }

      if (godziResult.type === GodziType.RatioPrize) {
        await godziDropService.showRatioWin()
        isGodziEnd = true
      }
      else {
        let newGodziRoundIndex = 0

        if (!isFirstEntryGodziTime) {
          newGodziRoundIndex = godziRoundIndex + 1
          isGodziEnd = newGodziRoundIndex >= godziResult.roundGameResult.length
          if (!isGodziEnd) {
            useGodziDropStore.setState({
              godziRoundIndex: newGodziRoundIndex,
            })
          }
        }
        if (!isGodziEnd) {
          await handleGodziResult(godziResult, newGodziRoundIndex)
        }
        else {
          await showGodziTotalWin(godziResult.type)
        }
      }

      useGodziDropStore.setState({ isGodziEnd: isGodziEnd })
    }),
  },
  actions: {
    checkAndAddBackSettledBalance: () => {
      usePlayerStore.getState().updateBalanceToSettledAmount()
    },
    onEntryFreeGameEntry: () => {
      freeGameService.showEnterFgScene()
      autoPlayService.cancel()
      resultService.saveRemainTable()
      multiplierService.saveRemainReel()
    },
    onEntryFreeGameEnd: () => {
      freeGameService.showExitFreeGame()
    },
    onExitFreeGameEnd: () => {
      resultService.setRemainTable()
      multiplierService.setRemainReel()
    },
    resetMainGameData: async () => {
      winBarService.closeTumbleWinBar()
      useGameStore.setState({
        currentFreeGameType: FreeGameType.MainGame,
      })
      useIsSpinning.setState(false)
    },
    getSpeedModeStatus: () => {
      const isSpeedMode = useSpeedStore.getState().isEnabled
      useGameStore.setState({ isThisSpinInSpeedMode: isSpeedMode,
      })
    },
    resetPlaceOrderData: async () => {
      winBarService.closeTumbleWinBar()
      useGameStore.setState({
        dropCurrentIndex: 0,
        currentPayout: 0,
        freeGameSpinCurrentIndex: 0,
      })
    },
    updateCurrentGameResult: async () => {
      const { dropCurrentIndex, isFreeGame, freeGameSpinCurrentIndex } = useGameStore.getState()
      const gameResult = getGameResult()
      let result
      if (isFreeGame) {
        result = gameResult.freeGame[freeGameSpinCurrentIndex]
      }
      else {
        result = gameResult.mainGame
        getFinalBalance()
      }
      const currentNormalTable = result.normalResult.dropTable
      const currentWinningReel = result.normalResult.winningReels
      const currentMultiplier = result.normalResult.multiplier
      let dropTable = [] as SlotSymbolInfo[][]

      if (result.dropResults[dropCurrentIndex]) {
        dropTable = result.dropResults[dropCurrentIndex].dropTable
      }
      const multiplier = result.normalResult.multiplier
      const currentPayout = result.normalResult.winningReels
        .map(el => el.payout)
        .reduce((a, b) => preciseAdd(a, b), 0)

      let scatterCount = 0
      result.normalResult.dropTable.forEach((reels) => {
        reels.forEach((element) => {
          if (element.symbol === SymbolType.Bonus || element.symbol === SymbolType.SuperBonus) {
            scatterCount++
          }
        })
      })

      if (dropCurrentIndex > 0) {
        result.dropResults[
          dropCurrentIndex - 1
        ].dropTable.forEach((reels) => {
          reels.forEach((element) => {
            if (element.symbol === SymbolType.Bonus || element.symbol === SymbolType.SuperBonus) {
              scatterCount++
            }
          })
        })
      }

      useGameStore.setState({
        dropCurrentIndex: dropCurrentIndex + 1,
        currentOriginPayout: currentPayout,
        currentPayout: preciseMultiply(currentPayout, multiplier),
        currentDropTable: dropTable,
        currentWinningReel,
        currentMultiplier,
        spinAccumulatedPayout: 0,
        currentNormalTable: currentNormalTable,
        scatterCount,
      })
    },
    updateFreeGameState: () => {
      winBarService.closeTumbleWinBar()

      useGameStore.setState({
        dropCurrentIndex: 0,
      })
      if (!useGameStore.getState().isFreeGame) {
        useGameStore.setState(state => ({
          isFreeGame: true,
          freeGameCount: state.baseFreeGameCount,
        }))
      }
      else {
        useGameStore.setState(state => ({
          freeGameSpinCurrentIndex: state.freeGameSpinCurrentIndex + 1,
        }))
      }
    },
    setIsMainGame: () => {
      useGameStore.setState({
        isFreeGame: false,
      })
    },
    resetSpinBtn: () => {
      useGameStore.setState({ isDisableEmergencyStop: false })
    },
    onExitGodziEnd: () => {
      useGodziDropStore.setState({ isGodziTime: false, isGodziEnd: false, godziRoundIndex: 0 })
    },
    enterGodziEntry: async () => {
      autoPlayService.cancel()
      const { godziResult } = useGodziDropStore.getState()
      if (godziResult.type === GodziType.FreeGame) {
        await godziDropService.enterFreeGamePrize()
      }
      else if (godziResult.type === GodziType.FreeSpin) {
        await godziDropService.enterFreeSpinPrize()

        useGodziDropStore.setState(state => ({
          freeSpinCount: state.godziResult.freeGameCount,
        }))
      }
    },
    initGodziDashBoard: () => {
      godziDashboardService.init()
    },
    showVoucherSelectPopup: () => {
      useVoucherStore.setState({ isShowVoucherSelectPopup: true })
    },
    updateVoucher: () => {
      const slotVoucher = useVoucherStore.getState().tempSlotVoucher
      if (slotVoucher !== null && slotVoucher !== undefined) {
        useVoucherStore.setState({
          currentVoucherId: slotVoucher.voucherId,
          totalPayout: slotVoucher.winAmount,
          fixedStake: slotVoucher.fixedStake,
          totalSpin: slotVoucher.freeSpin,
          remainSpin: slotVoucher.netFreeSpin,
          expireDate: slotVoucher.expireDate,
        })
      }
    },
    setIsVoucherTime: () => {
      useGameStore.setState({ isVoucherTime: true })
    },
    setExitVoucherTime: () => {
      useGameStore.setState({ isVoucherTime: false })
      useVoucherStore.setState({
        totalPayout: 0,
        currentVoucherId: '',
        fixedStake: 0,
        totalSpin: 0,
        remainSpin: 0,
        expireDate: new Date(),
      })
      autoPlayService.cancel()
    },
    showVoucherTotalWinPopup: () => {
      useVoucherStore.setState({ isShowVoucherTotalWinPopup: true })
    },
    setWinBoard: () => {
      // winBoardService.fgExitShowTotalPayout()
    },
  },
  guards: {
    hasDrop: () => {
      const currentDropTable = useGameStore.getState().currentDropTable
      return currentDropTable.length > 0
    },
    hasFreeGameNextRound: () => {
      const gameResult = getGameResult()
      return useGameStore.getState().freeGameSpinCurrentIndex < gameResult.freeGame.length - 1
    },
    isFreeGameEnd: () => {
      const gameResult = getGameResult()
      return useGameStore.getState().freeGameSpinCurrentIndex === gameResult.freeGame.length - 1
    },
    isFreeGameStart: () => {
      const gameResult = getGameResult()
      return (!useGameStore.getState().isFreeGame && gameResult.mainGame.freeGameType !== FreeGameType.MainGame && useGameStore.getState().isFirstFreeSpin())
    },
    hasRemainSpin: () => {
      return useAutoPlayStore.getState().isPlaying
    },
    isGodziDropStart: () => {
      const { isGodziTime, godziResult } = useGodziDropStore.getState()
      return !isGodziTime && godziResult.type !== GodziType.None
    },
    hasGodziDropNextRound: () => {
      const { isGodziTime, godziResult } = useGodziDropStore.getState()
      return isGodziTime && godziResult.type !== GodziType.None
    },
    isGodziFreeSpinPayoutZero: () => {
      const { godziResult } = useGodziDropStore.getState()
      return godziResult.type === GodziType.FreeSpin && godziResult.payout === 0
    },
    isGodziRatioPrize: () => {
      const { godziResult } = useGodziDropStore.getState()
      return godziResult.type === GodziType.RatioPrize
    },
    hasRemainSpinAndVoucher: () => {
      const slotVoucher = useVoucherStore.getState().tempSlotVoucher
      const currentVoucherId = useVoucherStore.getState().currentVoucherId
      return useAutoPlayStore.getState().isPlaying
        && slotVoucher !== null && slotVoucher !== undefined
        && slotVoucher.voucherId === currentVoucherId
    },
    hasRemainVoucher: () => {
      const slotVoucher = useVoucherStore.getState().tempSlotVoucher
      const currentVoucherId = useVoucherStore.getState().currentVoucherId
      return slotVoucher !== null && slotVoucher !== undefined && slotVoucher.voucherId === currentVoucherId
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5SwO5jAFwEIHsC2ARgHQCWAdiRgMRQCGeYASmLRAJ4DaADALqKgAHHLEokcZfiAAeiAExcAzEQCMAdgBsAFk0AOAJyquOgKx7ZqgDQg2cvXqILZp9cZ2z9XV8YC+3q6nRsfGI8WnIAcXowKgEAG1oAYzAAeQAnCDBU7j4kECERDDEJXJkEXVkVTWMFHU0NTQVPdSsbBGNNe1lldXU9Y1VVWSqFPV9-NExcQiJQiKiqAgBXNlwyRdhsyXzRcUlS3XUidy5VOwMT1Q6W2wrVfoU742UFXtl1MZAAyeCiDBwMWixADq5CoEHEYFIZAAbjgANaQr5BaZ-AHA8gIciwhK0QribKbXLbPHFUClAC07iIxjejXUsgediMPWuCGU-SIdjUGhpOh03U8HyRU2IqMBILIYIhUNhCKIwp+YvRZExMJwOJJBOUOUEwh2pOkN1U1IGXi0uh0A1ZI3sOgUymU52MNNUfKFE2Rov+4tB4LIkKx8MRHpFv29ytV2NxRQJsh1eT1JL2cldtye7QUzru-OMrNk8hUqmemmUmnMg20CndgVDcUSKXSmQACql1XBYFL-TKg-KQz860k0hlUi227BYJH1dH8bxCbqCkVkwgzJpOVx2eouFvenpS6yS8oiFV+uo+Y9FHp3n5Pn3pgOG8PR0lx1RMq3UkQ6xgAGY4VJ4Xsa37eJB0bEdW2fCdAw1GNZ14LZE0XEpEF3W49BePQOhpGpM2tTMiC3LcS2MdcFE0Fxq2+aYIBIWA6zYSIGGYWBFliag-QDNU5QVajaPoximDgViMEnGCZx4OcEwXXZkLZKpDzcAYej6MwXB0fdlB0Aic3Mcw6hMKtrx44gaLo+IGKiZjhM7TjZWDIDeLM2gLKYoS2NE6cyC1eNiSQslEG6ZQuG0801HcO56TzO5qUIho7S0E5DPGBziDAWISDwchPJs7tuNvVL0syshPI8zU4J8xCZP8hAujLBwjEGeQ1G0foNK0ox2V0y5XWqSjPSICBWwEHLAzylKBqG0rYIk+CiUqg1SjC1cGS4EsOlWvlIusRBnWCotlMMLcyKqPrQ0GnBho43L7KokzJugzzvIQ6SFrkdxDnQ3pTwMS8XE0VljE3I8yMaS1HVPWpTp+b9UnQASqBhuGogAZQEchkYBVIMEk3yqsNNliyIV0cMuZQunadTtoQQwlBGdRHHZRxanaKHpkRsABIAUTICAEdhjmUbRshuYgHH5qXTTgpeEYOiLExML5VkeluelVGOu4uD6XxrzIHAMngXJjOe-Ul3JDRqVpLh6UZPQGtZQwiDcJ5GgeU4HjeVniHIShjaTWTyUOTc3FItxLUUTxcyphpgs1kj3F0Vwqg9oz8pmMIyAE32-PxzSKgdKo5YdDRTWtdCifUbp0JOdpZD0O1PbDNEJSzvHSjecwiAh76SyMK3rR0Q55HizwhkUR0fBT8b7yHZsIPbFvXoQCGiZIotTH6DQTH7wejBeEfNDHrXJ9uga+PMgSrLYheJdLQ8RjuTT2Qddp-qpmpgqGflbfprQ3YbtKMpZT9vOE2slNL0yJqcOwBddxmGUNvI4u9Nw0gPvaI+yUT7nQENfWSQwGRE0wgcNQPQt5vzLp-BkB9baZnQTeca7NM5zRekuMGlRCEkTIloOu9shhHi8Aoe+poB4NwYVEEWODqqWkDgItw+Z6ROF6KyAehwyYVltmWMsshtbeCAA */
  id: 'sweetBomb',
  initial: 'init',
  states: {
    init: {
      on: {
        [MachineActionType.gameReady]: GameState.initGodziDashBoard,
      },
    },

    initGodziDashBoard: {
      entry: ['initGodziDashBoard'],
      on: {
        [MachineActionType.godziPopupClosed]: GameState.mainGame,
        [MachineActionType.enterVoucherMachine]: GameState.voucher,
      },
    },

    mainGame: {
      entry: ['resetMainGameData', 'checkAndAddBackSettledBalance'],
      on: {
        [MachineActionType.placeOrder]: [
          {
            target: GameState.placeOrderProcess,
          },
        ],
      },

    },

    totalWin: {
      invoke: {
        src: 'onTotalWin',
        onDone: [
          {
            guard: 'isFreeGameStart',
            target: GameState.freeGameEntry,
          },
          {
            guard: 'hasFreeGameNextRound',
            target: GameState.freeGame,
          },
          {
            guard: 'isFreeGameEnd',
            target: GameState.freeGameEnd,
          },
          {
            guard: 'isGodziDropStart',
            target: GameState.godziEntry,
          },
          {
            guard: 'hasGodziDropNextRound',
            target: GameState.godziTime,
          },
          {
            guard: 'hasRemainSpin',
            target: GameState.placeOrderProcess,
          },
          {
            target: GameState.mainGame,
          },
        ],
      },
      exit: ['resetSpinBtn'],
    },

    placeOrderProcess: {
      entry: ['resetPlaceOrderData', 'getSpeedModeStatus'],
      invoke: {
        src: 'onPlaceOrderProcess',
        onDone: GameState.displayGameResult,
        onError: {
          target: GameState.placeOrderFail,
          reenter: true,
        },
      },
    },

    placeOrderFail: {
      invoke: {
        src: 'onPlaceOrderFail',
        onDone: GameState.mainGame,
      },
    },

    displayGameResult: {
      entry: ['updateCurrentGameResult'],
      invoke: {
        src: 'onDisplayGameResult',
        onDone: [
          {
            target: GameState.elimination,
            guard: 'hasDrop',
          },
          {
            target: GameState.totalWin,
          },
        ],
      },
    },

    elimination: {
      invoke: {
        src: 'onElimination',
        onDone: GameState.drop,
      },
    },

    drop: {
      invoke: {
        src: 'onDrop',
        onDone: [
          {
            target: GameState.elimination,
            guard: 'hasDrop',
          },
          {
            target: GameState.totalWin,
          },
        ],
      },
    },
    freeGameEntry: {
      entry: ['onEntryFreeGameEntry'],
      on: {
        [MachineActionType.freeGameSpinStart]: {
          target: GameState.freeGame,
        },
      },
    },
    freeGame: {
      entry: ['updateFreeGameState', 'getSpeedModeStatus'],
      invoke: {
        src: 'onFreeGame',
        onDone: GameState.displayGameResult,
      },
    },

    freeGameEnd: {
      entry: ['onEntryFreeGameEnd'],
      on: {
        [MachineActionType.freeGameSpinEnd]: [
          {
            guard: 'isGodziDropStart',
            target: GameState.godziEntry,
          },
          {
            guard: 'hasGodziDropNextRound',
            target: GameState.godziTime,
          },
          {
            target: GameState.mainGame,
          },
        ],
      },
      exit: ['setIsMainGame', 'onExitFreeGameEnd'],
    },
    godziEntry: {
      entry: ['enterGodziEntry'],
      after: {
        100: { target: GameState.godziTime, guard: 'isGodziRatioPrize' } },
      on: {
        [MachineActionType.godziDropStart]:
          {
            target: GameState.godziTime,
          },
      },
    },
    godziTime: {
      entry: ['resetPlaceOrderData', 'getSpeedModeStatus'],
      invoke: {
        src: 'onGodziTime',
        onDone: [
          { target: GameState.godziEnd, guard: () => useGodziDropStore.getState().isGodziEnd },
          { target: GameState.displayGameResult },
        ],
      },
    },
    godziEnd: {
      after: {
        100: { target: GameState.mainGame, guard: 'isGodziFreeSpinPayoutZero' },

      },
      on: {
        [MachineActionType.godziDropEnd]:
          {
            target: GameState.mainGame,
          },
      },
      exit: ['onExitGodziEnd'],
    },
    voucher: {
      ...voucherMachine,
    },
  },
})

const updateCurrentDropResult = () => {
  const { dropCurrentIndex, isFreeGame, freeGameSpinCurrentIndex, scatterCount } = useGameStore.getState()
  const gameResult = getGameResult()
  let dropTable = [] as SlotSymbolInfo[][]
  let winningReel = [] as WinReelResult[]
  let currentMultiplier = 0
  let currentScatterCount = scatterCount

  let result
  if (isFreeGame) {
    result = gameResult.freeGame[freeGameSpinCurrentIndex]
  }
  else {
    result = gameResult.mainGame
  }
  if (result.dropResults[dropCurrentIndex]) {
    dropTable = result.dropResults[dropCurrentIndex].dropTable
    winningReel = result.dropResults[dropCurrentIndex - 1].winningReels
    currentMultiplier = result.dropResults[dropCurrentIndex - 1].multiplier
  }
  const multiplier = result.dropResults[dropCurrentIndex - 1].multiplier
  const currentOriginPayout = result.dropResults[dropCurrentIndex - 1].winningReels
    .map(el => el.payout)
    .reduce((a, b) => preciseAdd(a, b), 0)

  if (dropCurrentIndex > 0) {
    result.dropResults[
      dropCurrentIndex - 1
    ].dropTable.forEach((reels) => {
      reels.forEach((element) => {
        if (element.symbol === SymbolType.Bonus || element.symbol === SymbolType.SuperBonus) {
          currentScatterCount++
        }
      })
    })
  }

  useGameStore.setState({
    dropCurrentIndex: dropCurrentIndex + 1,
    currentDropTable: dropTable,
    currentWinningReel: winningReel,
    currentOriginPayout: currentOriginPayout,
    currentPayout: preciseMultiply(currentOriginPayout, multiplier),
    currentMultiplier,
    scatterCount: currentScatterCount,
  })
}

const getFinalBalance = () => {
  const balance = usePlayerStore.getState().balance
  const totalPayout = getGameResultFinalPayout()

  useGameStore.setState({
    finalBalance: preciseAdd(balance, totalPayout),
  })
}
const handleGodziResult = async (godziResult: GodziDropResultResponse, newGodziRoundIndex: number) => {
  await sleep(gameConfig.autoPlaySpinIntervalMs)
  if (godziResult.type === GodziType.FreeSpin) {
    useGodziDropStore.setState(state => ({
      freeSpinCount: state.freeSpinCount - 1,
    }))
  }
  const spin = spinService.spin()
  await multiplierService.clearReel()
  winBarService.closeTotalWinBar()
  useGameStore.setState({
    freeGameAccumulatedPayout: 0,
    multiplierReelInfo: godziResult.roundGameResult[newGodziRoundIndex].mainGame.multiplierReel,
  })
  await Promise.all([spin])
}
const showGodziTotalWin = async (godziResultType: GodziType) => {
  if (godziResultType === GodziType.FreeGame) {
    await godziDropService.showFreeGamePrizeWin()
  }
  else if (godziResultType === GodziType.FreeSpin) {
    await godziDropService.showFreeSpinPrizeWin()
  }
}
export const getGameResult = () => {
  const { isGodziTime, godziResult, godziRoundIndex } = useGodziDropStore.getState()
  if (isGodziTime) {
    return godziResult.roundGameResult[godziRoundIndex]
  }
  else {
    return useGameStore.getState().gameResult
  }
}
export const getGameResultFinalPayout = () => {
  const { isGodziTime, godziResult, godziRoundIndex } = useGodziDropStore.getState()
  if (isGodziTime) {
    return godziResult.roundGameResult[godziRoundIndex].totalPayout
  }
  else {
    return preciseMinus(useGameStore.getState().gameResult.totalPayout, godziResult.payout)
  }
}
export const sweetBombMachine = createActor(machine)
sweetBombMachine.start()
// sweetBombMachine.subscribe((state) => {
//   console.log('=== state ===', state.value)
// })
